import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Button, Spinner, Form } from 'react-bootstrap';
import { Check, AlertCircle, MapPin } from 'lucide-react';

const toResortSlug = (name, id) => {
  const slug = name.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug}-${id.substring(0, 8)}`;
};

// Search dropdown reused from LogVisitModal pattern — used to manually assign a resort
// to an unmatched Strava activity.
const ResortPicker = ({ resorts, selected, onSelect }) => {
  const [query, setQuery] = useState(selected?.name || '');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return resorts.filter(r => r.name.toLowerCase().includes(q)).slice(0, 6);
  }, [query, resorts]);

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 0, flex: 1 }}>
      <Form.Control
        size="sm"
        type="text"
        placeholder="Search resorts..."
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); if (selected) onSelect(null); }}
        onFocus={() => setOpen(true)}
        style={{ background: 'var(--pm-bg-page)', color: 'var(--pm-text)', borderColor: 'var(--pm-border)', fontSize: '0.8rem' }}
      />
      {open && filtered.length > 0 && (
        <div className="visit-resort-dropdown">
          {filtered.map(r => (
            <div
              key={r.id}
              className="visit-resort-dropdown-item"
              onClick={() => { onSelect(r); setQuery(r.name); setOpen(false); }}
            >
              {r.name}
              {r.country && <span style={{ color: 'var(--pm-text-muted)', fontSize: '0.75rem', marginLeft: '6px' }}>{r.country}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const formatDate = (d) => {
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
};

const StravaSyncModal = ({ show, onHide, syncResult, addVisit, importedStravaIds, resorts }) => {
  // Row state — each row has:
  //   { activityId, date, name, type, checked, resortId, resortName, assigned (bool for unmatched) }
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { saved, skipped, failed }

  useEffect(() => {
    if (!syncResult) { setRows([]); setResult(null); return; }

    const imported = importedStravaIds || new Set();
    const matchedRows = (syncResult.candidates || []).map(c => ({
      activityId: String(c.stravaActivityId),
      date: c.date,
      name: c.name,
      type: c.type,
      resortId: c.resortId,
      resortName: c.resortName,
      matched: true,
      alreadyImported: imported.has(String(c.stravaActivityId)),
      checked: !imported.has(String(c.stravaActivityId)),
    }));
    const unmatchedRows = (syncResult.unmatched || []).map(u => ({
      activityId: String(u.stravaActivityId),
      date: u.date,
      name: u.name,
      type: u.type,
      resortId: null,
      resortName: null,
      matched: false,
      alreadyImported: imported.has(String(u.stravaActivityId)),
      checked: false,
    }));
    setRows([...matchedRows, ...unmatchedRows]);
    setResult(null);
  }, [syncResult, importedStravaIds]);

  const toggleRow = (activityId) => {
    setRows(prev => prev.map(r => r.activityId === activityId ? { ...r, checked: !r.checked } : r));
  };

  const assignResort = (activityId, resort) => {
    setRows(prev => prev.map(r => {
      if (r.activityId !== activityId) return r;
      if (!resort) return { ...r, resortId: null, resortName: null, checked: false };
      return { ...r, resortId: resort.id, resortName: resort.name, checked: true };
    }));
  };

  const selectableCount = rows.filter(r => r.checked && r.resortId && !r.alreadyImported).length;

  const handleImport = async () => {
    setSaving(true);
    let saved = 0, failed = 0;
    for (const r of rows) {
      if (!r.checked || !r.resortId || r.alreadyImported) continue;
      try {
        await addVisit({
          resortId: r.resortId,
          resortName: r.resortName,
          date: r.date,
          snowRating: null,
          conditions: [],
          notes: '',
          source: 'strava',
          stravaActivityId: r.activityId,
        });
        saved++;
      } catch (err) {
        console.error('Failed to import Strava visit', r, err);
        failed++;
      }
    }
    const skipped = rows.filter(r => r.alreadyImported).length;
    setResult({ saved, failed, skipped });
    setSaving(false);
  };

  const matchedRows = rows.filter(r => r.matched);
  const unmatchedRows = rows.filter(r => !r.matched);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ background: 'var(--pm-bg-surface)', borderColor: 'var(--pm-border)' }}>
        <Modal.Title style={{ color: 'var(--pm-text)', fontSize: '1.1rem' }}>Import from Strava</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: 'var(--pm-bg-surface)', maxHeight: '70vh', overflowY: 'auto' }}>
        {!syncResult ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" variant="primary" /></div>
        ) : result ? (
          <div className="text-center py-4">
            <Check size={32} style={{ color: 'var(--pm-accent)' }} />
            <h6 className="mt-3" style={{ color: 'var(--pm-text)' }}>
              Imported {result.saved} visit{result.saved === 1 ? '' : 's'}
            </h6>
            {result.skipped > 0 && (
              <small style={{ color: 'var(--pm-text-muted)' }}>
                {result.skipped} already imported, skipped.
              </small>
            )}
            {result.failed > 0 && (
              <small style={{ color: 'var(--pm-text-muted)', display: 'block' }}>
                {result.failed} failed — check console.
              </small>
            )}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-4">
            <p style={{ color: 'var(--pm-text-muted)', margin: 0 }}>
              No ski or snowboard activities found in your Strava account.
            </p>
          </div>
        ) : (
          <>
            <p style={{ color: 'var(--pm-text-muted)', fontSize: '0.85rem' }}>
              Found {syncResult.totalActivities} ski/snowboard activities.
              Review matches below, then import. You can rate them later from your profile.
            </p>

            {matchedRows.length > 0 && (
              <>
                <h6 style={{ color: 'var(--pm-text)', fontSize: '0.9rem', marginTop: '12px' }}>
                  <Check size={14} style={{ color: 'var(--pm-accent)', marginRight: '4px' }} />
                  Matched to a resort ({matchedRows.length})
                </h6>
                <div className="d-flex flex-column gap-1 mb-3">
                  {matchedRows.map(r => (
                    <div key={r.activityId} className="strava-sync-row">
                      <Form.Check
                        type="checkbox"
                        checked={r.checked}
                        disabled={r.alreadyImported}
                        onChange={() => toggleRow(r.activityId)}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--pm-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.name}
                          {r.alreadyImported && <span style={{ color: 'var(--pm-text-muted)', fontWeight: 400, marginLeft: '8px' }}>(already imported)</span>}
                        </div>
                        <small style={{ color: 'var(--pm-text-muted)' }}>
                          {formatDate(r.date)} · {r.type} · <MapPin size={10} /> {r.resortName}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {unmatchedRows.length > 0 && (
              <>
                <h6 style={{ color: 'var(--pm-text)', fontSize: '0.9rem' }}>
                  <AlertCircle size={14} style={{ color: 'var(--pm-text-muted)', marginRight: '4px' }} />
                  No resort matched ({unmatchedRows.length})
                </h6>
                <p style={{ color: 'var(--pm-text-muted)', fontSize: '0.8rem' }}>
                  Pick a resort to import any of these.
                </p>
                <div className="d-flex flex-column gap-2">
                  {unmatchedRows.map(r => (
                    <div key={r.activityId} className="strava-sync-row">
                      <Form.Check
                        type="checkbox"
                        checked={r.checked}
                        disabled={r.alreadyImported || !r.resortId}
                        onChange={() => toggleRow(r.activityId)}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--pm-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.name}
                          {r.alreadyImported && <span style={{ color: 'var(--pm-text-muted)', fontWeight: 400, marginLeft: '8px' }}>(already imported)</span>}
                        </div>
                        <small style={{ color: 'var(--pm-text-muted)' }}>{formatDate(r.date)} · {r.type}</small>
                      </div>
                      {!r.alreadyImported && (
                        <ResortPicker
                          resorts={resorts}
                          selected={r.resortId ? { id: r.resortId, name: r.resortName } : null}
                          onSelect={resort => assignResort(r.activityId, resort)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ background: 'var(--pm-bg-surface)', borderColor: 'var(--pm-border)' }}>
        {result ? (
          <Button variant="primary" size="sm" onClick={onHide}>Done</Button>
        ) : (
          <>
            <Button variant="outline-secondary" size="sm" onClick={onHide} disabled={saving}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleImport}
              disabled={saving || selectableCount === 0}
            >
              {saving ? 'Importing...' : `Import ${selectableCount} visit${selectableCount === 1 ? '' : 's'}`}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export { toResortSlug };
export default StravaSyncModal;
